import { maskDeviceId, maskImei, parseImeiLines, type Device, type ImeiLineError } from "@fts/core";
import { DeviceFamilyBadge } from "./Teltonika";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { ConfirmDialog, CopyButton, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { RowAction } from "../components/RowAction";
import { Icon } from "../components/Icon";
import { useFeedback } from "../components/Toast";
import { PageHeader, Panel, Loading, Mono, EmptyState, Note } from "../components/ui";
import { PartialRegistrationError } from "../data/partial-registration";
import type { RegistrationResult } from "../data/provider";
import { useI18n } from "../i18n";
import { errorKey } from "../lib/errors";
import { downloadText, parseRegistrationLines, registrationCsvName, toCsv, type RegistrationLineError } from "../lib/format";
import { useSession } from "../state/session";
import { DeviceStatusBadge, LastSeen } from "./Devices";

/* ---------------- one-time secrets ---------------- */

/** Shows freshly generated device credentials once. Not dismissible by Escape or backdrop. */
export function RegistrationResultDialog({
  results,
  failure,
  onClose,
}: {
  results: RegistrationResult[];
  failure?: { deviceId: string; error: unknown } | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const notSynced = results.filter((r) => r.brokerSynced === false).map((r) => r.deviceId);
  const csv = () =>
    toCsv(
      ["device_id", "secret", "claim_code"],
      results.map((r) => [r.deviceId, r.secret, r.claimCode]),
    );
  return (
    <Dialog
      wide
      dismissible={false}
      title={t("register.resultTitle", { count: results.length })}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={() => downloadText(registrationCsvName(), csv())}>
            <Icon name="download" size={16} />
            {t("register.downloadCsv")}
          </button>
          <button type="button" className="button button-primary" onClick={onClose}>
            {t("secret.savedClose")}
          </button>
        </>
      }
    >
      <p className="warning-banner" role="alert">
        <Icon name="alert" size={18} />
        <span>{t("register.secretWarning")}</span>
      </p>
      {notSynced.length > 0 && (
        <p className="warning-banner" role="alert">
          <Icon name="alert" size={18} />
          <span>{t("register.brokerNotSynced", { devices: notSynced.join(", ") })}</span>
        </p>
      )}
      {failure && (
        <p className="form-error" role="alert">
          {t("register.partialFailure", { device: failure.deviceId, reason: t(errorKey(failure.error)) })}
        </p>
      )}
      <div className="table-wrap secrets-table">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">{t("device.id")}</th>
              <th scope="col">{t("secret.secret")}</th>
              <th scope="col">{t("secret.claimCode")}</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r) => (
              <tr key={r.deviceId}>
                <td className="nowrap">
                  <Mono>{r.deviceId}</Mono>
                </td>
                <td>
                  <span className="secret-cell">
                    <code className="secret-value">{r.secret}</code>
                    <CopyButton compact text={r.secret} label={t("secret.copySecret", { device: r.deviceId })} />
                  </span>
                </td>
                <td className="nowrap">
                  <span className="secret-cell">
                    <code className="secret-value secret-code">{r.claimCode}</code>
                    <CopyButton compact text={r.claimCode} label={t("secret.copyCode", { device: r.deviceId })} />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="field-hint">{t("register.labelHint")}</p>
    </Dialog>
  );
}

/**
 * A single one-time value (new secret after rotation or unblock, new claim code after release).
 * `notice` adds a second warning, e.g. when the MQTT server could not be updated.
 */
export function OneTimeValueDialog({
  title,
  label,
  value,
  warning,
  notice,
  onClose,
}: {
  title: string;
  label: string;
  value: string;
  warning: string;
  notice?: string;
  onClose: () => void;
}) {
  const { t } = useI18n();
  return (
    <Dialog
      dismissible={false}
      title={title}
      onClose={onClose}
      footer={
        <button type="button" className="button button-primary" onClick={onClose}>
          {t("secret.savedClose")}
        </button>
      }
    >
      <p className="warning-banner" role="alert">
        <Icon name="alert" size={18} />
        <span>{warning}</span>
      </p>
      {notice && (
        <p className="warning-banner" role="alert">
          <Icon name="alert" size={18} />
          <span>{notice}</span>
        </p>
      )}
      <div className="field">
        <span className="field-label">{label}</span>
        <span className="secret-cell secret-cell-lg">
          <code className="secret-value">{value}</code>
          <CopyButton text={value} label={`${t("common.copy")}: ${label}`} />
        </span>
      </div>
    </Dialog>
  );
}

/** Information that needs acknowledging (e.g. the MQTT server could not be updated). */
function NoticeDialog({ title, message, onClose }: { title: string; message: string; onClose: () => void }) {
  const { t } = useI18n();
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <button type="button" className="button button-primary" onClick={onClose} data-autofocus>
          {t("common.ok")}
        </button>
      }
    >
      <p className="warning-banner" role="alert">
        <Icon name="alert" size={18} />
        <span>{message}</span>
      </p>
    </Dialog>
  );
}

/* ---------------- registration ---------------- */

const LINE_REASON = {
  invalid_device_id: "register.lineInvalid",
  duplicate: "register.lineDuplicate",
  too_many_fields: "register.lineTooMany",
  // ADR 0034: Teltonika IMEI lines.
  format: "register.lineImeiFormat",
  checksum: "register.lineImeiChecksum",
  iccid: "register.lineIccid",
} as const;

const REGISTER_KINDS = ["fts", "teltonika"] as const;
type RegisterKind = (typeof REGISTER_KINDS)[number];
type RegisterRow = { deviceId: string; imei?: string | null; iccid?: string | null; hw?: string | null };

function RegisterDialog({
  existing,
  onClose,
  onDone,
}: {
  existing: Set<string>;
  onClose: () => void;
  onDone: (results: RegistrationResult[], failure: { deviceId: string; error: unknown } | null) => void;
}) {
  const { t } = useI18n();
  const { provider } = useSession();
  const [kind, setKind] = useState<RegisterKind>("fts");
  const [text, setText] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const parsed = useMemo(() => {
    // Teltonika units (ADR 0034): one IMEI per line (15 digits, Luhn), keyed TLT-<IMEI>.
    const p: { rows: RegisterRow[]; errors: (RegistrationLineError | ImeiLineError)[] } = kind === "teltonika" ? parseImeiLines(text) : parseRegistrationLines(text);
    // Already registered ids are reported per line before calling the server.
    const extra: RegistrationLineError[] = [];
    const rows = p.rows.filter((r) => {
      if (!existing.has(r.deviceId)) return true;
      const line = text.split(/\r?\n/).findIndex((l) => l.trim().toUpperCase().startsWith(kind === "teltonika" ? r.deviceId.slice(4) : r.deviceId));
      extra.push({ line: line + 1, text: r.deviceId, reason: "duplicate" });
      return false;
    });
    return { rows, errors: [...p.errors, ...extra].sort((a, b) => a.line - b.line) };
  }, [text, existing, kind]);

  const emptyError = touched && parsed.rows.length === 0 && parsed.errors.length === 0 ? t("register.empty") : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (parsed.rows.length === 0 || parsed.errors.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const results = await provider.registerDevices(parsed.rows);
      onDone(results, null);
    } catch (err) {
      if (err instanceof PartialRegistrationError) {
        onDone(err.registered, { deviceId: err.failedDeviceId, error: err });
        return;
      }
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      wide
      title={t("register.title")}
      description={t("register.description")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <span className="dialog-footer-note">{t("register.count", { count: parsed.rows.length })}</span>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy || parsed.errors.length > 0}>
            {busy ? t("common.working") : t("register.submit")}
          </button>
        </>
      }
    >
      <div className="field">
        <span className="field-label" id="register-kind-label">
          {t("register.kind")}
        </span>
        <div className="segmented" role="group" aria-labelledby="register-kind-label">
          {REGISTER_KINDS.map((k) => (
            <button key={k} type="button" className={k === kind ? "seg-on" : ""} aria-pressed={k === kind} onClick={() => setKind(k)}>
              {t(`register.kind.${k}`)}
            </button>
          ))}
        </div>
      </div>
      {kind === "teltonika" && <Note>{t("register.teltonikaNote")}</Note>}
      <FormField id="register-lines" label={kind === "teltonika" ? t("register.imeiLines") : t("register.lines")} hint={kind === "teltonika" ? t("register.imeiLinesHint") : t("register.linesHint")} error={emptyError}>
        <textarea
          {...fieldAria("register-lines", { hint: true, error: emptyError })}
          className="mono"
          rows={8}
          spellCheck={false}
          inputMode={kind === "teltonika" ? "numeric" : undefined}
          placeholder={kind === "teltonika" ? "356307042441013\n490154203237518,8990011122233344455" : "FTS-000123\nFTS-000124,350000000000124\nFTS-000125,350000000000125,8990000000000000125"}
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-autofocus
        />
      </FormField>
      {parsed.errors.length > 0 && (
        <ul className="line-errors" aria-label={t("register.lineErrors")}>
          {parsed.errors.map((er) => (
            <li key={`${er.line}-${er.text}`}>
              <span className="mono">{t("register.lineNo", { line: er.line })}</span> <span className="mono wrap">{er.text}</span> — {t(LINE_REASON[er.reason])}
            </li>
          ))}
        </ul>
      )}
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

/* ---------------- Inventory screen ---------------- */

type Filter = "all" | "inventory" | "claimed" | "revoked";
type Modal =
  | { kind: "register" }
  | { kind: "results"; results: RegistrationResult[]; failure: { deviceId: string; error: unknown } | null }
  | { kind: "rotate"; device: Device }
  | { kind: "revoke"; device: Device }
  | { kind: "release"; device: Device }
  | { kind: "value"; title: string; label: string; value: string; warning: string; notice?: string }
  | { kind: "notice"; title: string; message: string }
  | null;

export function Inventory() {
  const { t } = useI18n();
  const { provider, tenants, refresh } = useSession();
  const feedback = useFeedback();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState<Modal>(null);
  const tenantName = useMemo(() => new Map(tenants.map((x) => [x.id, x.name])), [tenants]);

  const load = useCallback(async () => {
    try {
      setDevices(await provider.listInventory());
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }, [provider]);

  useEffect(() => {
    void load();
  }, [load]);

  /** After a write: inventory list and the current tenant's data (rail, devices screen). */
  const reloadAll = async () => {
    await Promise.all([load(), refresh().catch(() => undefined)]);
  };

  const shown = useMemo(() => {
    const needle = q.trim().toUpperCase();
    return (devices ?? [])
      .filter((d) => {
        if (filter === "inventory" && d.tenantId !== null) return false;
        if (filter === "claimed" && d.tenantId === null) return false;
        if (filter === "revoked" && !d.revoked) return false;
        if (needle && ![d.deviceId, d.imei, d.iccid].some((x) => x?.toUpperCase().includes(needle))) return false;
        return true;
      })
      .sort((a, b) => a.deviceId.localeCompare(b.deviceId, "en", { numeric: true }));
  }, [devices, filter, q]);

  const counts = useMemo(() => {
    const list = devices ?? [];
    return { total: list.length, inventory: list.filter((d) => d.tenantId === null).length };
  }, [devices]);

  const dash = t("common.empty");

  let body;
  if (loadError)
    body = (
      <EmptyState icon="alert" title={t("common.loadError")} action={<button type="button" className="button button-secondary" onClick={() => void load()}>{t("common.retry")}</button>} />
    );
  else if (!devices)
    body = (
      <Loading shape="table" />
    );
  else if (shown.length === 0)
    body =
      devices.length === 0 ? (
        <EmptyState
          compact
          icon="box"
          title={t("inventory.empty")}
          action={
            <button type="button" className="button button-secondary" onClick={() => setModal({ kind: "register" })}>
              <Icon name="plus" size={16} />
              {t("inventory.register")}
            </button>
          }
        />
      ) : (
        <EmptyState
          compact
          icon="search"
          title={t("inventory.noMatch")}
          action={
            <button
              type="button"
              className="button button-secondary button-sm"
              onClick={() => {
                setFilter("all");
                setQ("");
              }}
            >
              {t("vehicles.clear")}
            </button>
          }
        />
      );
  else
    body = (
      <div className="table-wrap">
        <table className="table table-cards">
          <thead>
            <tr>
              <th scope="col">{t("device.id")}</th>
              <th scope="col" className="col-hide-sm">
                {t("device.family")}
              </th>
              <th scope="col">{t("inventory.tenant")}</th>
              <th scope="col">{t("device.status")}</th>
              <th scope="col" className="col-hide-sm">
                {t("device.lastSeen")}
              </th>
              <th scope="col" className="col-hide-md">
                {t("device.firmware")}
              </th>
              <th scope="col" className="col-hide-md">
                {t("device.imei")}
              </th>
              <th scope="col" className="col-actions">
                <span className="sr-only">{t("common.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shown.map((d) => (
              <tr key={d.deviceId}>
                <td className="nowrap cell-title" data-label={t("device.id")}>
                  {/* Lists show an IMEI (also inside TLT-<IMEI>) with its last 4 digits only (ADR 0034). */}
                  <Mono>{maskDeviceId(d.deviceId)}</Mono>
                </td>
                <td className="col-hide-sm" data-label={t("device.family")}>
                  <DeviceFamilyBadge device={d} />
                </td>
                <td data-label={t("inventory.tenant")}>
                  {d.tenantId ? (
                    <span>
                      {tenantName.get(d.tenantId) ?? dash}
                      {d.vehicleId && <span className="muted small"> · {t("inventory.onVehicle")}</span>}
                    </span>
                  ) : (
                    <span className="badge tone-info">{t("inventory.inStock")}</span>
                  )}
                </td>
                <td data-label={t("device.status")}>
                  <DeviceStatusBadge device={d} />
                </td>
                <td className="col-hide-sm nowrap" data-label={t("device.lastSeen")}>
                  <LastSeen iso={d.lastSeenAt} />
                </td>
                <td className="col-hide-md mono" data-label={t("device.firmware")}>
                  {d.fw ?? dash}
                </td>
                <td className="col-hide-md mono small" data-label={t("device.imei")}>
                  {maskImei(d.imei) ?? dash}
                </td>
                <td className="col-actions">
                  <span className="row-actions">
                    <RowAction icon="refresh" iconSize={17} label={t("inventory.rotate")} name={`${t("inventory.rotate")}: ${d.deviceId}`} onClick={() => setModal({ kind: "rotate", device: d })} />
                    <RowAction
                      icon={d.revoked ? "check" : "ban"}
                      iconSize={17}
                      label={d.revoked ? t("inventory.unrevoke") : t("inventory.revoke")}
                      name={`${d.revoked ? t("inventory.unrevoke") : t("inventory.revoke")}: ${d.deviceId}`}
                      danger={!d.revoked}
                      onClick={() => setModal({ kind: "revoke", device: d })}
                    />
                    {d.tenantId ? (
                      <RowAction icon="box" iconSize={17} label={t("inventory.release")} name={`${t("inventory.release")}: ${d.deviceId}`} onClick={() => setModal({ kind: "release", device: d })} />
                    ) : (
                      // Lost label: an unclaimed device gets a new claim code (and, like a release, a new secret).
                      <RowAction icon="key" iconSize={17} label={t("inventory.newClaimCode")} name={`${t("inventory.newClaimCode")}: ${d.deviceId}`} onClick={() => setModal({ kind: "release", device: d })} />
                    )}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  return (
    <>
      <PageHeader
        eyebrow={<span className="kind-tag">{t("tenant.platformAdmin")}</span>}
        title={t("inventory.title")}
        subtitle={devices ? t("inventory.subtitle", { total: counts.total, inventory: counts.inventory }) : undefined}
        actions={
          <button type="button" className="button button-primary" onClick={() => setModal({ kind: "register" })}>
            <Icon name="plus" size={16} />
            {t("inventory.register")}
          </button>
        }
      />
      <div className="filters" role="group" aria-label={t("inventory.filtersLabel")}>
        <div className="field field-inline">
          <label htmlFor="inv-filter">{t("inventory.filter")}</label>
          <select id="inv-filter" value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="all">{t("vehicles.all")}</option>
            <option value="inventory">{t("inventory.filterInventory")}</option>
            <option value="claimed">{t("inventory.filterClaimed")}</option>
            <option value="revoked">{t("inventory.filterRevoked")}</option>
          </select>
        </div>
        <div className="field field-inline">
          <label htmlFor="inv-q">{t("vehicles.filterSearch")}</label>
          <input id="inv-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("inventory.searchPlaceholder")} autoComplete="off" />
        </div>
      </div>
      <Panel flush>{body}</Panel>
      <Note>{t("inventory.note")}</Note>

      {modal?.kind === "register" && (
        <RegisterDialog
          existing={new Set((devices ?? []).map((d) => d.deviceId))}
          onClose={() => setModal(null)}
          onDone={(results, failure) => {
            setModal({ kind: "results", results, failure });
            void reloadAll();
          }}
        />
      )}
      {modal?.kind === "results" && <RegistrationResultDialog results={modal.results} failure={modal.failure} onClose={() => setModal(null)} />}
      {modal?.kind === "rotate" && (
        <ConfirmDialog
          title={t("rotate.title", { device: modal.device.deviceId })}
          confirmLabel={t("rotate.submit")}
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            const { secret, brokerSynced } = await provider.rotateDeviceSecret(modal.device.deviceId);
            setModal({
              kind: "value",
              title: t("rotate.resultTitle", { device: modal.device.deviceId }),
              label: t("secret.secret"),
              value: secret,
              warning: t("rotate.warning"),
              notice: brokerSynced ? undefined : t("rotate.brokerNotSynced"),
            });
          }}
        >
          <p>{t("rotate.body")}</p>
        </ConfirmDialog>
      )}
      {modal?.kind === "revoke" && (
        <ConfirmDialog
          title={modal.device.revoked ? t("revoke.unTitle", { device: modal.device.deviceId }) : t("revoke.title", { device: modal.device.deviceId })}
          confirmLabel={modal.device.revoked ? t("inventory.unrevoke") : t("inventory.revoke")}
          danger={!modal.device.revoked}
          onClose={() => setModal(null)}
          onConfirm={async () => {
            const device = modal.device.deviceId;
            const unblock = modal.device.revoked;
            const r = await provider.setDeviceRevoked(device, !unblock);
            await reloadAll();
            if (r.secret) {
              // The broker dropped the old credentials on block; the device needs this new secret.
              setModal({
                kind: "value",
                title: t("rotate.resultTitle", { device }),
                label: t("secret.secret"),
                value: r.secret,
                warning: t("revoke.unSecretWarning"),
                notice: r.brokerSynced ? undefined : t("revoke.unBrokerNotSynced", { device }),
              });
            } else if (!r.brokerSynced) {
              setModal({ kind: "notice", title: t("revoke.syncTitle"), message: t(unblock ? "revoke.unBrokerNotSynced" : "revoke.brokerNotSynced", { device }) });
            } else {
              setModal(null);
              feedback.ok(t(unblock ? "toast.deviceUnblocked" : "toast.deviceBlocked", { device }));
            }
          }}
        >
          <p>{modal.device.revoked ? t("revoke.unBody") : t("revoke.body")}</p>
        </ConfirmDialog>
      )}
      {modal?.kind === "release" && (
        <ConfirmDialog
          title={t(modal.device.tenantId ? "release.title" : "newClaim.title", { device: modal.device.deviceId })}
          confirmLabel={t(modal.device.tenantId ? "release.submit" : "newClaim.submit")}
          danger
          onClose={() => setModal(null)}
          onConfirm={async () => {
            const { claimCode, brokerSynced } = await provider.releaseDevice(modal.device.deviceId);
            setModal({
              kind: "value",
              title: t(modal.device.tenantId ? "release.resultTitle" : "newClaim.resultTitle", { device: modal.device.deviceId }),
              label: t("secret.claimCode"),
              value: claimCode,
              warning: t("release.warning"),
              notice: brokerSynced ? undefined : t("release.brokerNotSynced", { device: modal.device.deviceId }),
            });
            void reloadAll();
          }}
        >
          <p>
            {modal.device.tenantId
              ? t("release.body", { tenant: tenantName.get(modal.device.tenantId) ?? t("common.empty") })
              : t("newClaim.body")}
          </p>
        </ConfirmDialog>
      )}
      {modal?.kind === "value" && (
        <OneTimeValueDialog
          title={modal.title}
          label={modal.label}
          value={modal.value}
          warning={modal.warning}
          notice={modal.notice}
          onClose={() => {
            setModal(null);
            void reloadAll();
          }}
        />
      )}
      {modal?.kind === "notice" && <NoticeDialog title={modal.title} message={modal.message} onClose={() => setModal(null)} />}
    </>
  );
}
